// ============================================================================
// PÁGINA DEL REPARTIDOR (pública, celular): /repartidor/:token. Sin login — el enlace ES su llave (se rota
// desde Configuración si pierde el celular). «Tomar» es atómico en el servidor: si dos lo tocan a la vez,
// uno gana y el otro ve «Ya lo tomó X». Sondeo cada 10 s; sin red muestra el aviso y reintenta.
// Carlo 1-nov-2026: insignia «Foráneo» (la zona lo dice) y, con el flag del negocio, al entregar puede indicar
// con qué pagó el cliente si fue distinto a lo capturado (queda como reporte que la caja valida).
// ============================================================================
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Bike, MapPin, Phone, Check, Loader2, WifiOff, PackageCheck, Navigation, X } from 'lucide-react';
import { courierApi, money, hora, PAY_LABEL, type CourierView, type PublicOrder, type Payment } from '../api';

const PAGOS_REPORTE: Payment[] = ['efectivo', 'tarjeta', 'transferencia'];

export default function CourierPage() {
  const { token = '' } = useParams();
  const [v, setV] = useState<CourierView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Forma de pago elegida por pedido antes de «Entregado» (solo con flags.payment_report). Sin elegir = la capturada.
  const [pagoSel, setPagoSel] = useState<Record<number, Payment>>({});
  // Ficha de un pedido entregado al tocar su folio (Carlo 1-nov 19:04, flag del negocio `order_detail`).
  const [ficha, setFicha] = useState<PublicOrder | null>(null);

  const load = useCallback(async () => {
    try { setV(await courierApi.view(token)); setOffline(false); setError(null); }
    catch (e) {
      const st = (e as { status?: number }).status;
      if (st === 404) setError(e instanceof Error ? e.message : 'Enlace no válido');
      else setOffline(true);
    }
  }, [token]);
  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 10000);
    const on = () => load();
    window.addEventListener('online', on);
    return () => { window.clearInterval(t); window.removeEventListener('online', on); };
  }, [load]);

  const act = async (o: PublicOrder, kind: 'take' | 'deliver') => {
    setBusy(o.id); setAviso(null);
    try {
      if (kind === 'take') await courierApi.take(token, o.id);
      else {
        const p = pagoSel[o.id];
        const r = await courierApi.deliver(token, o.id, p && p !== o.payment ? p : undefined);
        if (r.payment_reported) setAviso(`Entregado. Avisamos a la caja que pagó con ${PAY_LABEL[r.payment_reported].toLowerCase()}.`);
      }
      if (navigator.vibrate) navigator.vibrate(30);
    } catch (e) { setAviso(e instanceof Error ? e.message : 'No se pudo'); }
    finally { setBusy(null); load(); }
  };

  if (error) {
    return <div className="flex min-h-screen flex-col items-center justify-center bg-canvas p-6 text-center">
      <Bike size={40} className="text-content-3" /><p className="mt-3 text-lg font-semibold text-content">{error}</p>
    </div>;
  }
  if (!v) return <div className="flex min-h-screen items-center justify-center bg-canvas"><Loader2 className="animate-spin text-content-3" /></div>;

  const color = v.courier.color || '#2563eb';
  const reporte = !!v.flags?.payment_report;
  const verFicha = !!v.flags?.order_detail;
  const mins = (a: string | null, b: string | null) => (a && b ? Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000)) : null);
  const mapa = (a: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}`;
  const card = (o: PublicOrder, kind: 'take' | 'deliver') => {
    const pago = pagoSel[o.id] || o.payment;
    return (
      <li key={o.id} className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-mono text-lg font-bold text-content">{v.ref_label} {o.external_ref}</span>
          <span className="text-xl font-bold text-content">{money(o.amount)}</span>
        </div>
        <p className={`mt-0.5 text-sm font-semibold ${o.payment === 'efectivo' ? 'text-emerald-600' : 'text-content-2'}`}>
          {o.payment === 'efectivo' ? `Cobrar ${money(o.amount)} en efectivo` : o.payment === 'pagado' ? 'Ya pagado — no cobrar' : `Pago con ${PAY_LABEL[o.payment].toLowerCase()}`}
        </p>
        {o.address && <a href={mapa(o.address)} target="_blank" rel="noreferrer" className="mt-2 flex items-start gap-1.5 text-base text-content"><MapPin size={18} className="mt-0.5 shrink-0 text-accent" />{o.address}</a>}
        <p className="mt-1 text-sm text-content-2">{[o.customer_name, o.zone, o.note].filter(Boolean).join(' · ')}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {o.zone_is_foreign && <span className="inline-block rounded-md bg-orange-500/15 px-2 py-0.5 text-sm font-semibold text-orange-700 dark:text-orange-300">Foráneo</span>}
          {o.courtesy && <span className="inline-block rounded-md bg-violet-500/10 px-2 py-0.5 text-sm font-semibold text-violet-700 dark:text-violet-300">Cortesía · sin cobro de envío</span>}
        </div>
        {kind === 'deliver' && reporte && (
          <div className="mt-3">
            <p className="mb-1 text-xs font-medium text-content-2">¿Con qué pagó el cliente?</p>
            <div className="flex gap-1.5">
              {PAGOS_REPORTE.map((p) => (
                <button key={p} type="button" onClick={() => setPagoSel({ ...pagoSel, [o.id]: p })}
                  className={`flex-1 rounded-xl border px-2 py-2 text-sm font-semibold ${pago === p ? 'border-accent bg-accent/10 text-accent' : 'border-line text-content-2'}`}>
                  {PAY_LABEL[p]}
                </button>
              ))}
            </div>
            {pago !== o.payment && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Se capturó como {PAY_LABEL[o.payment].toLowerCase()}; la caja recibirá el aviso del cambio.</p>}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button disabled={busy === o.id} onClick={() => act(o, kind)}
            className="flex h-14 flex-1 items-center justify-center gap-2 rounded-xl text-lg font-bold text-white active:scale-[.98] disabled:opacity-60"
            style={{ background: kind === 'take' ? color : '#16a34a' }}>
            {busy === o.id ? <Loader2 className="animate-spin" /> : kind === 'take' ? <><Bike size={22} />Tomar</> : <><Check size={22} />Entregado</>}
          </button>
          {o.customer_phone && <a href={`tel:${o.customer_phone}`} className="flex h-14 w-14 items-center justify-center rounded-xl border border-line text-content" aria-label="Llamar"><Phone size={22} /></a>}
          {kind === 'deliver' && o.address && <a href={mapa(o.address)} target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-xl border border-line text-content" aria-label="Mapa"><Navigation size={22} /></a>}
        </div>
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-canvas pb-24">
      <header className="sticky top-0 z-10 px-4 pb-3 pt-4 text-white shadow" style={{ background: color }}>
        <p className="text-xs font-medium uppercase tracking-wide text-white/80">{v.negocio?.name || 'Repartos'}</p>
        <h1 className="text-2xl font-bold">{v.courier.name}</h1>
      </header>
      {offline && <div className="flex items-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-white"><WifiOff size={16} />Sin conexión — reintentando…</div>}
      {aviso && <div className={`mx-4 mt-3 rounded-xl px-3 py-2 text-base font-semibold ${aviso.startsWith('Entregado') ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`}>{aviso}</div>}

      <main className="space-y-5 px-4 pt-4">
        {v.mios.length > 0 && (
          <section>
            <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-content"><Bike size={18} style={{ color }} />Mis pedidos ({v.mios.length})</h2>
            <ul className="space-y-3">{v.mios.map((o) => card(o, 'deliver'))}</ul>
          </section>
        )}
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-content"><PackageCheck size={18} className="text-amber-600" />Listos para salir ({v.listos.length})</h2>
          {v.listos.length ? <ul className="space-y-3">{v.listos.map((o) => card(o, 'take'))}</ul>
            : <p className="rounded-2xl border border-dashed border-line p-6 text-center text-content-3">No hay pedidos esperando. Se actualiza solo.</p>}
        </section>
        {v.entregados.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-semibold text-content-2">Entregados hoy</h2>
            <ul className="divide-y divide-line rounded-2xl border border-line bg-surface">
              {v.entregados.map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-content">
                  {verFicha
                    ? <button type="button" onClick={() => setFicha(o)} className="font-mono font-semibold text-accent underline decoration-dotted underline-offset-4">{o.external_ref}{o.zone_is_foreign && <span className="ml-1 text-xs font-semibold text-orange-600 no-underline dark:text-orange-300">FOR</span>}</button>
                    : <span className="font-mono">{o.external_ref}{o.zone_is_foreign && <span className="ml-1 text-xs font-semibold text-orange-600 dark:text-orange-300">FOR</span>}</span>}
                  <span className="text-content-2">{hora(o.delivered_at)}</span>
                  <span className="text-xs text-content-3">{o.payment_reported ? `${PAY_LABEL[o.payment_reported]} (por validar)` : PAY_LABEL[o.payment]}</span>
                  <span className="font-semibold">{money(o.amount)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      {ficha && (() => {
        const salio = mins(ficha.created_at, ficha.taken_at), ruta = mins(ficha.taken_at, ficha.delivered_at), total = mins(ficha.created_at, ficha.delivered_at);
        const fila = (k: string, val: ReactNode) => <div className="flex gap-3 py-0.5"><span className="w-24 shrink-0 text-content-3">{k}</span><span className="text-content">{val}</span></div>;
        return (
          <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 p-3 sm:items-center" onClick={() => setFicha(null)}>
            <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-surface p-5 text-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between">
                <div><p className="text-xs uppercase tracking-wide text-content-3">{v.ref_label}</p>
                  <p className="font-mono text-2xl font-bold text-content">{ficha.external_ref} <span className="font-sans text-lg">{money(ficha.amount)}</span></p></div>
                <button type="button" onClick={() => setFicha(null)} className="rounded-lg p-1 text-content-3" aria-label="Cerrar"><X size={22} /></button>
              </div>
              <p className="mt-3 font-semibold text-content">Ticket</p>
              {fila('Pago', ficha.payment_reported ? `${PAY_LABEL[ficha.payment_reported]} (por validar; se capturó ${PAY_LABEL[ficha.payment].toLowerCase()})` : PAY_LABEL[ficha.payment])}
              {fila('Entrega', [ficha.zone, ficha.delivery_fee ? `envío ${money(ficha.delivery_fee)}` : null, ficha.zone_is_foreign ? 'Foráneo' : null, ficha.courtesy ? 'Cortesía' : null].filter(Boolean).join(' · ') || '—')}
              {ficha.note && fila('Nota', ficha.note)}
              <p className="mt-3 font-semibold text-content">Tiempos</p>
              {fila('Capturado', hora(ficha.created_at))}
              {fila('Salió', `${hora(ficha.taken_at)}${salio != null ? ` · ${salio} min después de capturar` : ''}`)}
              {fila('Entregado', `${hora(ficha.delivered_at)}${ruta != null ? ` · ${ruta} min en ruta` : ''}${total != null ? ` · ${total} min en total` : ''}`)}
              <p className="mt-3 font-semibold text-content">Cliente</p>
              {fila('Nombre', ficha.customer_name || '—')}
              {ficha.customer_phone && fila('Teléfono', <a href={`tel:${ficha.customer_phone}`} className="text-accent">{ficha.customer_phone}</a>)}
              {fila('Domicilio', ficha.address ? <a href={mapa(ficha.address)} target="_blank" rel="noreferrer" className="text-accent">{ficha.address}</a> : '—')}
            </div>
          </div>
        );
      })()}

      <footer className="fixed inset-x-0 bottom-0 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur">
        <p className="text-center text-base font-semibold text-content">
          Hoy: {v.hoy.entregados} envío{v.hoy.entregados === 1 ? '' : 's'} · {money(v.hoy.efectivo)} en efectivo
          {v.hoy.comision > 0 && <span className="font-normal text-content-2"> · comisión {money(v.hoy.comision)}</span>}
        </p>
      </footer>
    </div>
  );
}
